import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { DropdownOptionType } from '@Components/Dropdown/Type';

import {
  CSBTypeOptions,
  TermsOfInvoiceOptions,
  TaxPaymentOptions,
  IsHavingBatteryOptions,
  onForward,
  thinkLogisticsInformationValidation,
  getWarehouses,
  batteryPackingOptions,
  batteryTypeOptions,
  addWarehouse,
} from './constants';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import SbTextField from '@Components/Textfield';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const ThinkLogisticsInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showFedExDetails,
  btnText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, thinkLogisticsDetails } = useSelector(
    (state: RootStateType) => state.adminShipment,
  );
  const HSNCodeValue = shipmentById?._packageId?.box?.[0]?.products || [];
  const thinkLogisticsSettings = useSelector((state: RootStateType) => state.user.carrierSetting.ithinklogistics);
  const [pickupAddressOptions, setPickupAddressOptions] = useState<DropdownOptionType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Hardcoded "Add Warehouse" option
  const addWarehouseOption: DropdownOptionType = {
    value: 'add_warehouse',
    display: '+ Add Warehouse',
  };

  // Combine add warehouse option with fetched warehouses
  const combinedPickupAddressOptions = [addWarehouseOption, ...pickupAddressOptions];

  const fetchWarehouses = async (accountNumber: string) => {
    try {
      const warehouses = await getWarehouses(accountNumber);
      setPickupAddressOptions(warehouses);
    } catch (error) {
      console.error('Failed to fetch warehouses', error);
    }
  };

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  useEffect(() => {
    const accountNumber = accountOptions?.[0]?.value.split('-')[0];
    if (accountNumber && shipmentById?.buyingPricing?.code === 'ithinklogistics') {
      fetchWarehouses(accountNumber);
    }
  }, [shipmentById, accountOptions]);

  const handleWarehouseChange = async (value: string[]) => {
    const selectedValue = value[0] || '';

    if (selectedValue === 'add_warehouse') {
      // Handle "Add Warehouse" option click
      if (!values.accountNo?.length) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: 'Please select an iThink Logistics Account first',
          }),
        );
        setFieldValue('pickup_address_id', '');
        return;
      }

      setIsLoading(true);
      try {
        // Extract account number from the selected account
        const accountNumber = values.accountNo.split('-')[0];
        const shipmentNumber = shipmentById?.shipmentNumber || '';
        await addWarehouse({ accountNo: accountNumber, shipmentNumber });

        // Refresh the warehouses list after successful addition
        await fetchWarehouses(accountNumber);

        // Optionally select the newly added warehouse automatically
        // You might want to adjust this based on your API response
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success!',
            subTitle: 'Warehouse added successfully!',
          }),
        );
      } catch (error) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: error?.response?.data?.msg || 'Failed to add warehouse. Please try again.',
          }),
        );
      } finally {
        setIsLoading(false);
        setFieldValue('pickup_address_id', '');
      }
    } else {
      // Normal warehouse selection
      setFieldValue('pickup_address_id', selectedValue);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      csb_type: '',
      terms_of_invoice: '',
      tax_payment_option: '',
      is_having_battery: '',
      pickup_address_id: '',
      battery_packing: '',
      battery_type: '',
      HSNCode: HSNCodeValue?.[0]?.HSNCode || '',
      ...initialStates,
    },
    validationSchema: thinkLogisticsInformationValidation,
    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit } = formik;

  // Check if battery fields should be shown
  const showBatteryFields = values.is_having_battery === 'yes';

  if (!showFedExDetails) return null;

  return (
    <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
      <Typography variant="h6" gutterBottom>
        iThink Logistics Information
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="accountNo"
            label="iThink Logistics Account"
            required
            placeholder="Select"
            name="accountNo"
            value={values.accountNo ? [values.accountNo] : []}
            options={thinkLogisticsSettings?.accountNo?.length ? thinkLogisticsSettings?.accountNo : accountOptions}
            onChange={value => {
              setFieldValue('accountNo', value[0]);
            }}
            error={errors.accountNo as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable || isLoading}
            id="pickup_address_id"
            label="Warehouse Address"
            required
            placeholder="Select"
            name="pickup_address_id"
            value={values.pickup_address_id ? [values.pickup_address_id] : []}
            options={combinedPickupAddressOptions}
            onChange={handleWarehouseChange}
            error={errors.pickup_address_id as string}
          />
          {isLoading && (
            <Typography variant="caption" sx={{ color: 'primary.main', mt: 1 }}>
              Adding warehouse, please wait...
            </Typography>
          )}
        </Grid>

        {/* Rest of your existing JSX remains the same */}
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="csb_type"
            label="CSB Type"
            required
            placeholder="Select"
            name="csb_type"
            value={values.csb_type ? [values.csb_type] : []}
            options={CSBTypeOptions}
            onChange={val => {
              const selected = val[0] || '';
              setFieldValue('csb_type', selected);
            }}
            error={errors.csb_type as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="terms_of_invoice"
            label="Terms of Invoice"
            required
            placeholder="Select"
            name="terms_of_invoice"
            value={values.terms_of_invoice ? [values.terms_of_invoice] : []}
            options={TermsOfInvoiceOptions}
            onChange={val => setFieldValue('terms_of_invoice', val[0] || '')}
            error={errors.terms_of_invoice as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="tax_payment_option"
            label="Tax Payment Option"
            required
            placeholder="Select"
            name="tax_payment_option"
            value={values.tax_payment_option ? [values.tax_payment_option] : []}
            options={TaxPaymentOptions}
            onChange={val => setFieldValue('tax_payment_option', val[0] || '')}
            error={errors.tax_payment_option as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="is_having_battery"
            label="Is Having Battery"
            required
            placeholder="Select"
            name="is_having_battery"
            value={values.is_having_battery ? [values.is_having_battery] : []}
            options={IsHavingBatteryOptions}
            onChange={val => {
              const selected = val[0] || '';
              setFieldValue('is_having_battery', selected);
              // Clear battery fields when battery option changes to "no"
              if (selected !== 'yes') {
                setFieldValue('battery_packing', 'NA');
                setFieldValue('battery_type', 'NA');
              }
            }}
            error={errors.is_having_battery as string}
          />
        </Grid>

        {/* Conditionally render battery fields */}
        {showBatteryFields && (
          <>
            <Grid item xs={12} md={6}>
              <SBDropdown
                disabled={!isEditable}
                id="battery_packing"
                label="Battery Packing"
                required
                placeholder="Select"
                name="battery_packing"
                value={values.battery_packing ? [values.battery_packing] : []}
                options={batteryPackingOptions}
                onChange={val => setFieldValue('battery_packing', val[0] || '')}
                error={errors.battery_packing as string}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SBDropdown
                disabled={!isEditable}
                id="battery_type"
                label="Battery Type"
                required
                placeholder="Select"
                name="battery_type"
                value={values.battery_type ? [values.battery_type] : []}
                options={batteryTypeOptions}
                onChange={val => setFieldValue('battery_type', val[0] || '')}
                error={errors.battery_type as string}
              />
            </Grid>
          </>
        )}
        <Grid container item columnSpacing={10} rowSpacing={2}>
          <Grid item xs={12} md={6}>
            <SbTextField
              placeholder="Enter HSN Code"
              label="HSN Code"
              required
              onChange={e => setFieldValue('HSNCode', e.target.value)}
              name="HSNCode"
              value={values.HSNCode}
              error={errors.HSNCode as string}
            />
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SbButton
            disabled={!isEditable || isLoading}
            variant="contained"
            sx={{ borderRadius: 1 }}
            fullWidth
            onClick={() => handleSubmit()}
          >
            {btnText}
          </SbButton>
        </Grid>

        {onClose && (
          <Grid item xs={12} sm={6} md={3}>
            <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        )}
      </Grid>

      {thinkLogisticsDetails?.response && (
        <Grid container mt={3}>
          <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component="span"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 1,
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default ThinkLogisticsInformation;
